import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DataApiResponseLegacy,
  ServiceDeskApproveModel,
  ServiceDeskAttachmentModel,
  ServiceDeskMessageModel,
  ServiceDeskRecipientAddModel,
  ServiceDeskReporterCandidateModel,
  ServiceDeskResolutionModel,
  ServiceDeskTicketAddModel,
  ServiceDeskTicketEditModel,
  ServiceDeskTicketListModel,
  ServiceDeskTicketModel,
  ServiceDeskTicketsQuery,
} from '@digilize/shared/definitions/src';

/** The company service desk (PAUL-3646). One set of endpoints for both audiences; the token decides the scope. */
@Injectable({ providedIn: 'root' })
export class ServiceDeskApiService {
  readonly TICKETS_URL = '/api/v1.0/service-desk/tickets';
  readonly REPORTER_CANDIDATES_URL = '/api/v1.0/service-desk/reporter-candidates';

  constructor(private http: HttpClient) {}

  /* DataTables envelope in camelCase (`recordsFiltered`), hence DataApiResponseLegacy. The params go
     through untouched: the backend binds `search.value` and a repeated `companies=a&companies=b`. */
  getTickets(params: ServiceDeskTicketsQuery) {
    return this.http.get<DataApiResponseLegacy<ServiceDeskTicketListModel[]>>(this.TICKETS_URL, {
      params: { ...params },
    });
  }

  getTicket(id: string) {
    return this.http.get<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}`);
  }

  /** Company staff file for their own company; an admin for any. Returns the new ticket. */
  addTicket(data: ServiceDeskTicketAddModel) {
    return this.http.post<ServiceDeskTicketModel>(this.TICKETS_URL, data);
  }

  /**
   * Both sides reply here; the backend stamps the side from the caller's role. `send_email` is an admin flag, so
   * it is only on the wire when given.
   */
  addMessage(id: string, body: string, sendEmail?: boolean) {
    const data = sendEmail === undefined ? { body } : { body, send_email: sendEmail };
    return this.http.post<ServiceDeskMessageModel>(`${this.TICKETS_URL}/${id}/messages`, data);
  }

  /**
   * One multipart request: every file as `files`, plus `message_id` when the files belong to the caller's own reply.
   * At most 10 files and 100 MB together; splitting a larger pick is the caller's job. Emits the upload progress.
   */
  addAttachments(id: string, files: File[], messageId?: string | null) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));

    if (messageId) {
      formData.append('message_id', messageId);
    }

    const request = new HttpRequest('POST', `${this.TICKETS_URL}/${id}/attachments`, formData, {
      reportProgress: true,
    });
    return this.http.request<ServiceDeskAttachmentModel[]>(request);
  }

  // Admin-only from here on; the backend answers 403 for anyone else. Each write returns the whole ticket.
  editTicket(id: string, data: ServiceDeskTicketEditModel) {
    return this.http.put<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}`, data);
  }

  /** null or '' unlinks and clears every Jira field. */
  linkJira(id: string, jiraKey: string | null) {
    return this.http.put<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}/jira`, { jira_key: jiraKey });
  }

  setResolution(id: string, data: ServiceDeskResolutionModel) {
    return this.http.put<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}/resolution`, data);
  }

  approve(id: string, data: ServiceDeskApproveModel) {
    return this.http.post<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}/resolution/approve`, data);
  }

  /** null clears the reporter. */
  setReporter(id: string, reporterUserId: string | null) {
    return this.http.put<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}/reporter`, {
      reporter_user_id: reporterUserId,
    });
  }

  /**
   * Moves the ticket to a company's desk (PAUL-3684), e.g. one that came in by email from nobody the backend could
   * place. The shop and register are cleared, and the reporter unless they are a Moderator of that company. The people
   * in copy are cleared too when the ticket had a company already, and kept when it gets its first (PAUL-3776).
   */
  setCompany(id: string, companyId: string) {
    return this.http.put<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}/company`, { company_id: companyId });
  }

  /**
   * Puts one address in copy (PAUL-3776). An address already in copy changes nothing, and one removed earlier comes
   * back, with the new name when one is given.
   */
  addRecipient(id: string, email: string, name?: string | null) {
    const data: ServiceDeskRecipientAddModel = name ? { email, name } : { email };
    return this.http.post<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}/recipients`, data);
  }

  /** Remembered: a later mail that names the address does not bring it back, only addRecipient does. */
  removeRecipient(id: string, recipientId: string) {
    return this.http.delete<ServiceDeskTicketModel>(`${this.TICKETS_URL}/${id}/recipients/${recipientId}`);
  }

  /** The Moderators (and above) of the ticket's company, sorted by name; empty for a ticket without a company. */
  getReporterCandidates(id: string) {
    return this.http.get<ServiceDeskReporterCandidateModel[]>(`${this.TICKETS_URL}/${id}/reporter-candidates`);
  }

  /** The same list for the new-ticket form, before a ticket exists. */
  getCompanyReporterCandidates(companyId: string) {
    return this.http.get<ServiceDeskReporterCandidateModel[]>(this.REPORTER_CANDIDATES_URL, {
      params: { company: companyId },
    });
  }
}
